// Die Farben der vier Knotenzustaende — **eine** Tabelle fuer beide Ausgabewege.
//
// Der Baum ist Canvas, die Legende darunter ist DOM. Beide muessen dasselbe
// sagen, und `scenes` und `ui` kennen sich nicht (Gate FG-04) — ihr
// gemeinsamer Nenner ist diese Schicht. Die Farbwerte zusaetzlich ins CSS zu
// schreiben waere genau die stille Abweichung, die niemand meldet: die Legende
// sagt gruen, der Baum malt gold.
//
// Der Kontrast ist die eigentliche Botschaft. Warm (gold, gruen) heisst „hier
// geht noch etwas", kalt (petrol, cyan) heisst „fertig". Ein Spieler mit 47
// Knoten soll das aus dem Augenwinkel sehen, ohne einen einzigen Tooltip.

import type { NodeState } from '../data/types';

export interface NodeStyle {
  /** Fuellung des Knotenfeldes. */
  fill: number;
  /** Rahmen — beim kaufbaren Knoten der eigentliche Traeger der Botschaft. */
  border: number;
  /** Akzent: Ecke, Fortschrittsleiste, Stufenzahl, Punkt in der Legende. */
  accent: number;
  /** Deckkraft des Icons. Der zu teure Knoten bleibt lesbar, tritt aber zurueck. */
  iconAlpha: number;
  /** Sichtbarer Name — deutsch, mit Umlauten. */
  label: string;
}

export const NODE_STYLE: Record<NodeState, NodeStyle> = {
  buyable: {
    fill: 0xf0c437, border: 0x35c458, accent: 0x59f07a, iconAlpha: 1, label: 'kaufbar',
  },
  available: {
    fill: 0x4a4228, border: 0x6d6238, accent: 0xf0c437, iconAlpha: 0.72, label: 'zu teuer',
  },
  locked: {
    fill: 0x1e2731, border: 0x0a1016, accent: 0x3d4956, iconAlpha: 1, label: 'gesperrt',
  },
  // Der Rand bleibt bewusst gedaempft: der ausgebaute Knoten ist erledigt und
  // soll nicht lauter rufen als der kaufbare. Die Leiste darin darf hell sein,
  // sie ist klein.
  max: {
    fill: 0x123640, border: 0x35908c, accent: 0x5fe6d8, iconAlpha: 1, label: 'ausgebaut',
  },
};

/**
 * Die Reihenfolge der Legende: erst das Handeln, dann das Warten, dann das
 * Ziel, zuletzt das Erledigte.
 */
export const NODE_STATE_ORDER: readonly NodeState[] = ['buyable', 'available', 'locked', 'max'];

/** Eine Zahl aus der Tabelle als CSS-Farbe — der Weg ins DOM. */
export function cssColor(value: number): string {
  return '#' + value.toString(16).padStart(6, '0');
}

/**
 * Warmer Schleier ueber einem angeschlagenen Brocken — multipliziert auf die
 * Erzfarbe.
 *
 * Er ist der Grund, warum die Erzfarben **unter** ihm geprueft werden (Gate
 * FG-42): ein Multiply zieht alle Toene in dieselbe Richtung und schiebt damit
 * genau die Paare zusammen, die vorher gerade noch auseinanderlagen.
 */
export const ROCK_DAMAGE_TINT = 0xffb0a0;

/**
 * Wie ein **reicher** Brocken sich vom gewoehnlichen abhebt: der Koerper geht
 * ins Dunkle, die Adern leuchten staerker (`RICH_VEIN_BOOST` in `asteroid.ts`).
 *
 * Das trug frueher ein zweiter Satz Bilder — acht dunkle Brocken mit weissen
 * Adern neben acht hellen. Als Toenung kostet es keine einzige Datei, und der
 * Unterschied bleibt derselbe: nur die Adern nehmen die Erzfarbe voll an.
 *
 * Er steht **neben** `FIELD_TINTS`, nicht darin: gegen jene Liste misst FG-42
 * den Abstand der Erzfarben, und ein Grauton dort schnuerte die Erzpalette
 * grundlos ein.
 */
export const ROCK_RICH_TINT = 0xb4b4b4;

/**
 * Farbe je **Rang** — je gefaehrlicher, desto heisser.
 *
 * Das stand hier schon als Kommentar, waehrend die Datenstruktur etwas anderes
 * tat: sie war eine Karte je **ID** und kannte nur die fuenf Arten von Akt I.
 * Truemmergeschuetz, Leerlaeufer, Kuppelwaechter, Bastion und Archivar fielen
 * deshalb alle auf denselben Rueckfallton — und weil sie zugleich dieselbe
 * Textur trugen, war in Akt II der traege Waechter vom Koloss nicht zu
 * unterscheiden. Eine Feldliste von Hand, die still veraltet: dieselbe Falle,
 * gegen die FG-31 und FG-36 argumentieren.
 *
 * Am Rang statt an der Art haengt sie aus zwei Gruenden. Erstens sagt sie damit
 * etwas: **die Farbe ist die Gefahr, die Form die Art, die Variante die ID** —
 * drei Merkmale, wie im Baum und auf der Karte. Zweitens bleibt `FIELD_TINTS`
 * bei fuenf Werten; zehn eigene Toene waeren zehn zusaetzliche Verbotszonen fuer
 * die Edelsteinfarben (FG-42). Nebeneinander sieht der Spieler die beiden
 * Saetze ohnehin nie — eine Aera, ein Feld (FG-44).
 */
export const HOSTILE_TINT: Record<number, number> = {
  1: 0x9aa4b0,
  2: 0xd8b45a,
  3: 0xff9a4a,
  4: 0xff5a4a,
  5: 0xc04ae0,
};

/** Rueckfall fuer einen Rang ausserhalb der Tabelle. */
export const HOSTILE_TINT_FALLBACK = 0xff8a6a;

/** Der Ton eines Ranges. Ein Rang ohne Eintrag faellt auf den Rueckfall. */
export const hostileTint = (tier: number): number =>
  HOSTILE_TINT[Math.floor(tier)] ?? HOSTILE_TINT_FALLBACK;

/** Farbe je Anomalieart — der Sturm warm, das Nullfeld kalt und tot. */
export const ANOMALY_TINT: Record<string, number> = {
  ion_storm: 0xffc46a,
  null_pocket: 0x58d8c8,
};

/**
 * Der Ring des Suchsignals — dasselbe Blau, das die Kiste traegt.
 *
 * Dieselbe Farbe und nicht eine eigene: der Hinweis nennt sie „die blaue
 * Kiste", und ein zweiter Ton dafuer waere die Stelle, an der beide still
 * auseinanderlaufen.
 */
export const SIGNAL_TINT = 0x2f6bb0;

/**
 * Alles, was **neben** einem Brocken im selben Bild leuchtet.
 *
 * Die Edelsteinfarben muessen sich davon absetzen: die Schwarmmutter ist
 * violett, der Ionensturm warm — ein Kristall, der wie ein Gegner aussieht,
 * kostet den Spieler einen Anflug.
 */
export const FIELD_TINTS: readonly number[] = [
  ...Object.values(HOSTILE_TINT), HOSTILE_TINT_FALLBACK, ...Object.values(ANOMALY_TINT),
  SIGNAL_TINT,
];
